/**
 * The person page's moves: what's next (same Write / Done / Skip actions as
 * the list), then what was done or skipped, by whom and when.
 */
import { useState } from "react";
import { CalendarClock, Check, SkipForward } from "lucide-react";
import { cn } from "@/lib/utils";
import { Empty, Section, dayLabel, timeAgo } from "@/features/command/ui";
import { MoveItem } from "./MoveItem";
import { memberName } from "./shared";
import type { Handle, Move } from "./types";

const HISTORY_SHOWN = 6;

export function MovesCard({
  moves, email, handles, onChanged, stopped,
}: {
  moves: Move[];
  email: string | null;
  handles: Handle[];
  onChanged: () => void;
  /** Why no new moves get planned ("ended" / "hidden"), if that's the case. */
  stopped?: "ended" | "hidden" | null;
}) {
  const [all, setAll] = useState(false);
  const open = moves.filter((m) => m.status === "open").sort((a, b) => a.due_date.localeCompare(b.due_date));
  const history = moves.filter((m) => m.status !== "open")
    .sort((a, b) => String(b.done_at ?? b.due_date).localeCompare(String(a.done_at ?? a.due_date)));
  const shown = all ? history : history.slice(0, HISTORY_SHOWN);

  return (
    <Section title="Moves" icon={CalendarClock} count={open.length}>
      {open.length === 0 ? (
        <Empty title="Nothing planned">
          {stopped === "hidden"
            ? "Hidden people get no follow-ups. Unhide them to start their rhythm."
            : stopped === "ended"
              ? "This relationship has ended, so there are no follow-ups. Set them back to Active to start again."
              : "Their next follow-up appears here as soon as it's planned."}
        </Empty>
      ) : (
        <div className="space-y-2">
          {open.map((m) => (
            <MoveItem key={m.id} move={m} email={email} handles={handles} onChanged={onChanged} compact />
          ))}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Done before</div>
          <ul className="space-y-1.5">
            {shown.map((m) => {
              const done = m.status === "done";
              const when = m.done_at ?? m.due_date;
              return (
                <li key={m.id} className="flex items-start gap-2.5 text-[13px]">
                  <span className={cn("mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full", done ? "bg-emerald-500/15 text-emerald-300" : "bg-zinc-800 text-zinc-500")}>
                    {done ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : <SkipForward className="h-2.5 w-2.5" />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className={done ? "text-zinc-200" : "text-zinc-500"}>
                      {m.label}
                      <span className="text-zinc-500">{done ? "" : " (skipped)"}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {m.done_by_name ? `${memberName(m.done_by_name).split(/\s+/)[0]}, ` : ""}
                      <span title={dayLabel(when, { month: "short", day: "numeric", year: "numeric" })}>{m.done_at ? timeAgo(m.done_at) : dayLabel(when)}</span>
                    </div>
                    {m.note && <div className="mt-0.5 text-xs text-zinc-400">{m.note}</div>}
                  </div>
                </li>
              );
            })}
          </ul>
          {history.length > HISTORY_SHOWN && (
            <button type="button" onClick={() => setAll((v) => !v)} className="mt-2 text-xs font-medium text-indigo-300 hover:text-indigo-200">
              {all ? "Show fewer" : `Show all ${history.length}`}
            </button>
          )}
        </div>
      )}
    </Section>
  );
}
